import TravelPassConsumptionCard from '@/components/manage/travel-passes/TravelPassConsumptionCard';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import {
  getManageFlowBookingById,
  getManageFlowTravelAccountsMap,
} from '@/features/manage/manageActions';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import type { TravelAccountConsumptionDTO } from '@/types/dto/agent';
import {
  getBookingNonTripOfferFulfillments,
  isTravelPassBooking,
} from '@/utils/booking';
import { Typography } from '@fleet/webshop-ui';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ViewBookedTripsDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  consumptions: TravelAccountConsumptionDTO[];
}

const ViewBookedTripsDialog: FC<ViewBookedTripsDialogProps> = ({
  isOpen,
  onOpenChange,
  consumptions,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingBookingId, setLoadingBookingId] = useState<string | null>(null);

  const handleViewDetails = useCallback(
    async (bookingId: string) => {
      if (!bookingId) return;

      setLoadingBookingId(bookingId);

      try {
        const booking = await dispatch(
          getManageFlowBookingById(bookingId)
        ).unwrap();

        if (!booking) {
          toast.error(<TransAlert i18nKey="fetchingBookingFailed" />);
          return;
        }

        const fulfillments = getBookingNonTripOfferFulfillments(booking);
        if (isTravelPassBooking(booking) && fulfillments.length) {
          await dispatch(getManageFlowTravelAccountsMap(fulfillments)).unwrap();
        }

        onOpenChange(false);
        navigate('/manage-booking/details');
      } catch (error) {
        toast.error(<TransAlert i18nKey="fetchingBookingFailed" />);
      } finally {
        setLoadingBookingId(null);
      }
    },
    [dispatch, navigate, onOpenChange]
  );

  const content =
    consumptions.length === 0 ? (
      <div className="flex items-center justify-center p-8">
        <Typography variant="body1" className="text-neutral">
          <TransText i18nKey="noConsumptionsFound" />
        </Typography>
      </div>
    ) : (
      <div className="flex flex-col gap-4 p-4">
        {consumptions.map((consumption, index) => {
          const isLoading = loadingBookingId === consumption.bookingId;
          const isDisabled =
            !consumption.bookingId || loadingBookingId !== null;

          return (
            <TravelPassConsumptionCard
              key={`${consumption.bookingId}-${index}`}
              consumption={consumption}
              onViewDetails={handleViewDetails}
              isLoading={isLoading}
              isDisabled={isDisabled}
            />
          );
        })}
      </div>
    );

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={onOpenChange}
      title={<TransText i18nKey="travelPassUsage" />}
      content={content}
    />
  );
};

export default ViewBookedTripsDialog;
